import { projectForReaders } from '../connectedBible'
import { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRouteState } from '../routeState'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Download,
  Edit3,
  Flag,
  RotateCcw,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import type { StoryProject } from '../types'
import { choiceAvailable, describeCondition } from '../storyLogic'
import {
  advancePlaythrough,
  createPlaythrough,
  rewindPlaythrough,
  takeRoute,
} from '../playthrough'
import { LogicValueInput } from './StoryLogicEditor'
import { downloadPlaytestReport } from '../productionExport'

type Props = {
  project: StoryProject
  startNodeId?: string | null
  onClose: () => void
  onEditNode: (nodeId: string) => void
}

export function PlayPreview({
  project: sourceProject,
  startNodeId,
  onClose,
  onEditNode,
}: Props) {
  const project = useMemo(
    () => projectForReaders(sourceProject),
    [sourceProject],
  )
  const [play, setPlay] = useState(() =>
    createPlaythrough(project, startNodeId ?? project.startNodeId),
  )
  const { nodeId, originId, lineIndex, history, state } = play
  const [stateOpen, setStateOpen] = useRouteState<boolean>('play-state', false)
  const [coverage, setCoverage] = useState({
    scenes: new Set([nodeId]),
    choices: new Set<string>(),
  })
  const dialogRef = useRef<HTMLDialogElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const node =
    project.nodes.find((candidate) => candidate.id === nodeId) ??
    project.nodes[0]
  const block = node.blocks[lineIndex]
  const destination = (id: string | null) =>
    project.nodes.find((candidate) => candidate.id === id)
  const variables = project.variables ?? []
  const allChoices = node.kind === 'ending' ? [] : node.choices
  const available = (choice: (typeof node.choices)[number]) =>
    choiceAvailable(choice, state, variables)
  const choices = allChoices.filter(
    (choice) => choice.unavailableMode !== 'hide' || available(choice),
  )
  const hasContinuation =
    node.kind !== 'ending' &&
    !allChoices.length &&
    Boolean(destination(node.nextId))
  const totalChoices = project.nodes
    .filter((scene) => scene.kind !== 'ending')
    .reduce((sum, scene) => sum + scene.choices.length, 0)

  useLayoutEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null
    dialogRef.current?.showModal()
    contentRef.current?.focus()
    return () => {
      previousFocus?.focus()
    }
  }, [])

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
    contentRef.current?.focus()
  }, [nodeId, lineIndex])

  useEffect(() => {
    setCoverage((current) => ({
      scenes: new Set([...current.scenes, nodeId]),
      choices: new Set([
        ...current.choices,
        ...history
          .filter((frame) => frame.choiceId)
          .map((frame) => frame.nodeId + ':' + frame.choiceId),
      ]),
    }))
  }, [nodeId, history])

  const jump = (choiceId: string) =>
    setPlay((current) => takeRoute(project, current, choiceId))
  const advance = () =>
    setPlay((current) => advancePlaythrough(project, current))
  const goBack = () => setPlay(rewindPlaythrough)
  const restart = (opening = false) => {
    const nextOrigin = opening ? project.startNodeId : originId
    setPlay(createPlaythrough(project, nextOrigin))
  }
  return (
    <dialog
      ref={dialogRef}
      className="play-preview"
      aria-label="Story playtest"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => {
        if ((event.target as HTMLElement).closest('input, select, textarea'))
          return
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          goBack()
        }
        if (!block && choices.length && /^[1-9]$/.test(event.key)) {
          event.preventDefault()
          const choice = choices[Number(event.key) - 1]
          if (choice) jump(choice.id)
        }
        if (
          (event.key === 'Enter' || event.key === ' ') &&
          !(event.target as HTMLElement).closest(
            'button, input, select, a, textarea',
          )
        ) {
          event.preventDefault()
          advance()
        }
      }}
    >
      <div className="preview-stage">
        <div className="preview-stage__grain" />
        <header className="preview-toolbar">
          <div>
            <span>PLAYTEST</span>
            <strong>{project.title}</strong>
          </div>
          <div className="preview-toolbar__scene">
            <small>
              SCENE {String(project.nodes.indexOf(node) + 1).padStart(2, '0')}
            </small>
            <span>{node.title}</span>
          </div>
          <nav>
            <button
              type="button"
              aria-label="Story state and route coverage"
              title="Story state and route coverage"
              aria-expanded={stateOpen}
              aria-controls="playtest-state-panel"
              onClick={() => setStateOpen(!stateOpen)}
            >
              <SlidersHorizontal size={17} />
            </button>
            <button
              type="button"
              onClick={goBack}
              disabled={!history.length && !lineIndex}
              title="Previous line or choice"
              aria-label="Previous line or choice"
            >
              <ArrowLeft size={17} />
            </button>
            <button
              type="button"
              onClick={() => restart()}
              title="Restart playtest"
              aria-label="Restart playtest"
            >
              <RotateCcw size={16} />
            </button>
            <button
              type="button"
              onClick={() => onEditNode(node.id)}
              title="Edit this scene"
              aria-label="Edit this scene"
            >
              <Edit3 size={16} />
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Close playtest"
              aria-label="Close playtest"
            >
              <X size={19} />
            </button>
          </nav>
        </header>
        <div className="playtest-context">
          <span>
            {originId === project.startNodeId
              ? 'From the opening'
              : 'From the selected scene'}
          </span>
          <span>
            {history.length + 1}{' '}
            {history.length === 0 ? 'scene visited' : 'scenes visited'}
          </span>
          {originId !== project.startNodeId && (
            <button type="button" onClick={() => restart(true)}>
              Start from opening <ArrowRight size={12} />
            </button>
          )}
        </div>
        <div className="preview-empty-stage" aria-hidden="true">
          <span>THE STORY IS YOURS TO FOLLOW</span>
          <i />
          <small>{node.title}</small>
        </div>
        <div
          className="preview-overlay"
          tabIndex={-1}
          ref={contentRef}
          aria-live="polite"
        >
          {play.errors.length ? (
            <div className="preview-unfinished" role="alert">
              <AlertCircle size={24} />
              <h2>A story change needs attention</h2>
              {play.errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
              <p>
                No changes were applied. Open Review to repair the story rule.
              </p>
              <button type="button" onClick={() => onEditNode(node.id)}>
                <Edit3 size={15} /> Return to writing
              </button>
            </div>
          ) : block ? (
            <button
              type="button"
              className={'preview-line preview-line--' + block.kind}
              onClick={advance}
            >
              {block.kind === 'dialogue' && (
                <header>
                  <strong>{block.speaker || 'CHARACTER'}</strong>
                  {block.direction && <span>({block.direction})</span>}
                </header>
              )}
              <p>
                {block.text || <em>This line is waiting to be written.</em>}
              </p>
              <small>
                {lineIndex + 1} / {node.blocks.length} · CLICK, SPACE, OR ENTER
                TO CONTINUE
              </small>
            </button>
          ) : choices.length ? (
            <div className="preview-choices">
              <span>WHAT DO YOU CHOOSE?</span>
              {choices.map((choice, index) => (
                <button
                  type="button"
                  key={choice.id}
                  onClick={() => jump(choice.id)}
                  disabled={!destination(choice.targetId) || !available(choice)}
                >
                  <i>{String(index + 1).padStart(2, '0')}</i>
                  <span>
                    {choice.label || 'Untitled choice'}
                    {!destination(choice.targetId) && (
                      <small>No destination assigned</small>
                    )}
                    {!available(choice) && (
                      <small>
                        Locked ·{' '}
                        {(choice.conditions ?? [])
                          .map((condition) =>
                            describeCondition(condition, variables),
                          )
                          .join(
                            choice.conditionMode === 'any' ? ' or ' : ' and ',
                          )}
                      </small>
                    )}
                  </span>
                </button>
              ))}
              {!allChoices.some(
                (choice) => available(choice) && destination(choice.targetId),
              ) && (
                <p className="preview-blocked-copy">
                  No choice can be taken with the current story state. Change
                  test values in the state panel, go back, or revise this scene.
                </p>
              )}
              {choices.some((choice) => !destination(choice.targetId)) && (
                <button
                  type="button"
                  className="repair-preview"
                  onClick={() => onEditNode(node.id)}
                >
                  <Edit3 size={14} /> Connect unfinished choices
                </button>
              )}
            </div>
          ) : allChoices.length ? (
            <div className="preview-unfinished">
              <AlertCircle size={24} />
              <h2>No choices are available</h2>
              <p>
                Every choice is hidden by its conditions. Try a different story
                state or revise the rules to give the player a way forward.
              </p>
              <button type="button" onClick={() => setStateOpen(true)}>
                Inspect story state
              </button>
            </div>
          ) : node.kind === 'ending' ? (
            <div className="preview-ending">
              <Flag size={22} />
              <span>THE END</span>
              <p>You reached an ending.</p>
              <button type="button" onClick={() => restart()}>
                Play again
              </button>
            </div>
          ) : hasContinuation ? (
            <button
              type="button"
              className="preview-continue"
              onClick={advance}
            >
              Continue to {destination(node.nextId)?.title}
              <ArrowRight size={15} />
            </button>
          ) : (
            <div className="preview-unfinished">
              <AlertCircle size={24} />
              <h2>
                {node.nextId
                  ? 'This route needs a destination'
                  : 'The next page is unwritten'}
              </h2>
              <p>
                {node.nextId
                  ? 'The linked scene could not be found. Reconnect it to keep the story moving.'
                  : 'This scene has no continuation or choices yet. Return to the draft to decide what happens next.'}
              </p>
              <button type="button" onClick={() => onEditNode(node.id)}>
                <Edit3 size={15} /> Keep writing
              </button>
            </div>
          )}
        </div>
        {stateOpen && (
          <aside
            className="playtest-state"
            id="playtest-state-panel"
            aria-label="Playtest state"
          >
            <header>
              <div>
                <strong>Behind this playthrough</strong>
                <small>Test values · your draft stays saved as written</small>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Close story state"
                onClick={() => {
                  setStateOpen(false)
                  contentRef.current?.focus()
                }}
              >
                <X size={16} />
              </button>
            </header>
            <section>
              <h3>Story state</h3>
              <p>
                Scene changes run on entry. Back restores the values at the
                previous decision. Restart uses the starting values.
              </p>
              {originId !== project.startNodeId && (
                <p className="state-origin-note">
                  Started at this scene with default values. Set up the state
                  you want to test below.
                </p>
              )}
              {variables.length ? (
                variables.map((variable) => (
                  <label className="playtest-variable" key={variable.id}>
                    <span>
                      {variable.name || 'Unnamed variable'}
                      <small>{variable.description}</small>
                    </span>
                    <LogicValueInput
                      type={variable.type}
                      value={state[variable.id]}
                      label={`Test value for ${variable.name}`}
                      onChange={(value) =>
                        setPlay((current) => ({
                          ...current,
                          state: { ...current.state, [variable.id]: value },
                        }))
                      }
                    />
                  </label>
                ))
              ) : (
                <p>
                  No variables yet. Add them in Story bible when your choices
                  need to remember something.
                </p>
              )}
            </section>
            <section>
              <h3>Coverage this session</h3>
              <div className="coverage-counts">
                <span>
                  <strong>
                    {coverage.scenes.size} / {project.nodes.length}
                  </strong>{' '}
                  scenes visited
                </span>
                <span>
                  <strong>
                    {coverage.choices.size} / {totalChoices}
                  </strong>{' '}
                  choices taken
                </span>
              </div>
              <p>
                Includes earlier attempts until you close playtest. This records
                what you tested; it does not prove every state is reachable.
              </p>
            </section>
            <section>
              <h3>Current route</h3>
              <ol className="playtest-trace">
                {[...history, play].map((frame, index) => {
                  const scene = project.nodes.find(
                    (item) => item.id === frame.nodeId,
                  )
                  return (
                    <li key={index}>
                      <strong>{scene?.title}</strong>
                      {frame.choiceId && index < history.length && (
                        <small>
                          {
                            scene?.choices.find(
                              (choice) => choice.id === frame.choiceId,
                            )?.label
                          }
                        </small>
                      )}
                    </li>
                  )
                })}
              </ol>
              <button
                type="button"
                className="state-export"
                onClick={() => downloadPlaytestReport(project, play, coverage)}
              >
                <Download size={14} /> Export playtest report
              </button>
            </section>
          </aside>
        )}
        <footer className="playtest-hint">
          <span>← Previous</span>
          <span>Space / Enter · Continue</span>
          <span>1–9 · Choose</span>
          <span>Esc · Back to writing</span>
        </footer>
      </div>
    </dialog>
  )
}
